import { pxToEm } from 'util/pxToEm';
import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import React, { ReactElement, ReactNode, useState } from 'react';

export interface MultiSwitchProps<T extends readonly string[] = string[]> {
  options: T;
  selection: T[number];
  icons?: ReactNode[];
  onChange: (selection: T[number]) => void;
  uncontrolled?: boolean;
  variant?: 'surface' | 'input' | 'tempered';
  className?: string;
}

export const MultiSwitch = <T extends readonly string[] = string[]>({
  options,
  selection,
  onChange,
  icons = [],
  uncontrolled = false,
  variant = 'surface',
  className,
}: MultiSwitchProps<T>): ReactElement => {
  const [isControlled] = useState(!uncontrolled);
  const [selectionState, setSelection] = useState<T[number]>(selection);

  const position = options.indexOf(isControlled ? selection : selectionState);
  const numOptions = options.length;

  const handleSelect = (option: T[number]) => {
    onChange(option);
    if (!isControlled) setSelection(option);
  };

  return (
    <Box
      role="menu"
      fontSize="clamp(12px, 1rem, 22px)"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      position="relative"
      sx={{
        background: theme => theme.palette.common.white,
        borderRadius: variant === 'input' ? 0 : 37,
        boxShadow: 'none',
        border: theme => `1px solid ${theme.palette.neutral.dark}`,
        height: variant === 'tempered' ? 36 : 50,
        userSelect: 'none',
        width: 'auto',
        margin: 'auto',
        '& .MultiSwitch-button': {
          WebkitTapHighlightColor: 'rgba(0,0,0,0)',
          outline: 'none',
          '&:focus-visible': {
            outline: theme => `2px auto ${theme.palette.primary.main}`,
            outlineOffset: 6,
          },
          padding:
            variant === 'surface'
              ? '0 20px'
              : variant === 'tempered'
              ? '8px 20px'
              : 0,
          '& .MultiSwitch-buttonText': {
            fontFamily: 'Poppins-Regular, Poppins',
            fontWeight: 400,
            fontSize: pxToEm(14),
            color: theme =>
              variant === 'surface'
                ? theme.palette.text.primary
                : theme.palette.primary.main,
            transitionDuration: '0.1s',
            '&.selected': {
              color: theme => theme.palette.common.white,
            },
          },
        },
      }}
      className={className}
    >
      <Box
        aria-hidden
        sx={[
          {
            background: theme => theme.palette.primary.main,
            position: 'absolute',
            transition: '0.4s',
            width: `${100 / numOptions}%`,
            height: variant === 'tempered' ? 36 : 50,
            borderRadius: variant === 'input' ? 0 : 37,
            marginLeft: `${(position / numOptions) * 100}%`,
            zIndex: 100,
            display: position === -1 ? 'none' : 'block',
          },
          selection === 'Hidden' && {
            '.HideSchoolSwitch &': {
              background: theme => theme.palette.error.light,
            },
          },
        ]}
      ></Box>
      {options.map((option, i) => {
        const isSelected = isControlled
          ? selection === option
          : selectionState === option;

        return (
          <Box
            flex="1 1"
            overflow="hidden"
            key={option}
            role="menuitemradio"
            aria-checked={isSelected}
            zIndex={200}
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            whiteSpace="nowrap"
            className={clsx('MultiSwitch-button', {
              selected: isSelected,
            })}
            style={{
              cursor: isSelected ? 'default' : 'pointer',
            }}
            tabIndex={0}
            onKeyPress={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelect(option);
              }
            }}
            onClick={() => handleSelect(option)}
          >
            {icons[i] && (
              <Box pr={10} lineHeight={0}>
                {icons[i]}
              </Box>
            )}
            <Typography
              className={clsx('MultiSwitch-buttonText', {
                selected: isSelected,
              })}
            >
              {option}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
